import { reactive, ref, computed, watch } from 'vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import ImovelAPI from 'dashboard/api/imoveis';
import AgentPublicProfileAPI from 'dashboard/api/agentPublicProfile';
import {
  IMOVEL_FORM_OWNER,
  normalizeImovelPayload,
  normalizeOptionalInteger,
} from 'dashboard/helper/lais/imovelPayload';
import {
  COMPLETION_HANDOFF_CHECKLIST,
  DRAFT_KEY,
  FEATURES_LIST,
  PROPERTY_TYPES,
  buildBaseState,
  mapFinalidade,
} from './constants';

const normalizeLookupValue = value =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();

const firstQueryValue = value => {
  if (Array.isArray(value)) {
    return value.find(
      item => item !== null && item !== undefined && item !== ''
    );
  }

  return value;
};

const hasQueryValue = value =>
  value !== null && value !== undefined && value !== '';

const queryValue = (query = {}, keys = []) => {
  const value = keys
    .map(key => firstQueryValue(query[key]))
    .find(hasQueryValue);

  return hasQueryValue(value) ? String(value).trim() : '';
};

const queryListValue = (query = {}, keys = []) =>
  keys
    .flatMap(key => {
      const value = query[key];
      return Array.isArray(value) ? value : [value];
    })
    .flatMap(value => {
      if (typeof value === 'string') return value.split(/[\n,]/);
      if (value === null || value === undefined) return [];
      return [String(value)];
    })
    .map(value => value.trim())
    .filter(Boolean);

const positiveInteger = value => {
  const parsed = parseInt(String(value || ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const subtypeSelectionForLookup = normalized =>
  PROPERTY_TYPES.map(item => {
    const subtype = item.subtipos.find(
      option => normalizeLookupValue(option) === normalized
    );
    return subtype ? { tipoImovel: item.tipo, subTipoImovel: subtype } : null;
  }).find(Boolean);

const propertyTypeSelection = value => {
  const normalized = normalizeLookupValue(value);
  if (!normalized) return {};

  const directType = PROPERTY_TYPES.find(
    item => normalizeLookupValue(item.tipo) === normalized
  );
  if (directType) return { tipoImovel: directType.tipo };

  const matchedSubtype = subtypeSelectionForLookup(normalized);
  if (matchedSubtype) return matchedSubtype;

  return { tipoImovel: String(value).trim() };
};

const subtypeSelection = value => {
  const normalized = normalizeLookupValue(value);
  if (!normalized) return {};

  const matchedSubtype = subtypeSelectionForLookup(normalized);
  if (matchedSubtype) return matchedSubtype;

  return { subTipoImovel: String(value).trim() };
};

const transactionFromQuery = value => {
  const normalized = normalizeLookupValue(value);
  if (!normalized) return '';

  const isSale = normalized.includes('venda') || normalized.includes('sale');
  const isRent =
    normalized.includes('aluguel') ||
    normalized.includes('locacao') ||
    normalized.includes('rent');

  if (isSale && isRent) return 'Venda e Aluguel';
  if (isRent) return 'Aluguel';
  if (isSale) return 'Venda';
  return '';
};

export const buildDeepLinkPrefill = (query = {}) => {
  const patch = {};
  const assign = (field, keys) => {
    const value = queryValue(query, keys);
    if (value) patch[field] = value;
  };

  assign('titulo', ['titulo', 'title', 'nome']);
  assign('descricao', ['descricao', 'description']);
  assign('categoriaImovel', ['categoria_imovel', 'categoria', 'category']);
  assign('cep', ['cep', 'postal_code', 'zipcode']);
  assign('estado', ['estado', 'uf', 'state']);
  assign('cidade', ['cidade', 'city']);
  assign('bairro', ['bairro', 'neighborhood']);
  assign('endereco', ['endereco', 'logradouro', 'address', 'street']);
  assign('numero', ['numero', 'number', 'address_number']);
  assign('complemento', ['complemento', 'complement', 'unit']);
  assign('zona', ['zona', 'zone']);
  assign('areaUtil', ['area_util', 'areaUtil', 'usable_area']);
  assign('areaTotal', ['area_total', 'areaTotal', 'total_area']);
  assign('qtdDormitorios', ['quartos', 'dormitorios', 'bedrooms']);
  assign('qtdBanheiros', ['banheiros', 'bathrooms']);
  assign('qtdSuites', ['suites']);
  assign('qtdVagas', ['vagas', 'vagas_garagem', 'parking_spots']);
  assign('iptu', ['iptu', 'valor_iptu']);
  assign('valorCondominio', ['condominio', 'valor_condominio', 'condo_fee']);
  assign('anoConstrucao', ['ano_construcao', 'construction_year']);
  assign('videoUrl', ['video_url', 'video']);
  assign('tourVirtualUrl', ['tour_url', 'tour_virtual_url', 'tour']);
  assign('latitude', ['latitude', 'lat']);
  assign('longitude', ['longitude', 'lng']);

  const typeValue = queryValue(query, ['tipo_imovel', 'tipo', 'property_type']);
  const subtypeValue = queryValue(query, [
    'subtipo_imovel',
    'subtipo',
    'sub_tipo',
    'property_subtype',
  ]);
  Object.assign(patch, propertyTypeSelection(typeValue));
  Object.assign(patch, subtypeSelection(subtypeValue));

  const transaction = transactionFromQuery(
    queryValue(query, ['transacao', 'transaction_type', 'finalidade', 'deal'])
  );
  if (transaction) patch.transactionType = transaction;

  const salePrice = queryValue(query, [
    'preco_venda',
    'valor_venda',
    'sale_price',
  ]);
  const rentPrice = queryValue(query, [
    'preco_aluguel',
    'valor_aluguel',
    'rent_price',
  ]);
  const genericPrice = queryValue(query, ['preco', 'valor', 'price']);
  if (salePrice) patch.precoVenda = salePrice;
  if (rentPrice) patch.precoAluguel = rentPrice;
  if (genericPrice && !salePrice && !rentPrice) {
    if (patch.transactionType === 'Aluguel') {
      patch.precoAluguel = genericPrice;
    } else {
      patch.precoVenda = genericPrice;
    }
  }

  const images = queryListValue(query, [
    'midias',
    'midia',
    'imagens',
    'imagem',
    'image_url',
    'foto_url',
    'cover_url',
  ]);
  if (images.length) patch.selectedImages = images;

  const profileId = positiveInteger(
    queryValue(query, ['selected_profile_id', 'profile_id', 'perfil_id'])
  );

  return { statePatch: patch, profileId };
};

const loadDraft = () => {
  try {
    const stored = localStorage.getItem(DRAFT_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    localStorage.removeItem(DRAFT_KEY);
    return null;
  }
};

// Estado central do fluxo "Criar imóvel" (espelha useCreatePostState.ts do
// imudei, adaptado ao Chatwoot: usa uploadHelper, ImovelAPI e o seletor de
// perfil postável). Composable instanciado uma vez pela página pai e passado
// aos 3 passos.
export function useCreateImovelState(accountId) {
  const state = reactive(buildBaseState());

  const step = ref(1);
  const showOptional = ref(false);
  const isUploadingImages = ref(false);
  const isPublishing = ref(false);
  const errorMessage = ref('');
  const deepLinkPrefillApplied = ref(false);

  // Perfis onde o corretor pode publicar (próprio + setores).
  const profiles = ref([]);
  const selectedProfileId = ref(null);
  const isLoadingProfiles = ref(false);
  const profilesError = ref('');

  // Hidrata rascunho salvo localmente.
  const draft = loadDraft();
  if (draft && typeof draft === 'object') {
    Object.keys(state).forEach(key => {
      if (draft[key] !== undefined) state[key] = draft[key];
    });
  }

  // Auto-save do rascunho enquanto o corretor edita.
  watch(
    state,
    () => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(state));
      } catch {
        // localStorage indisponível — ignora silenciosamente.
      }
    },
    { deep: true }
  );

  const updateState = patch => {
    Object.assign(state, patch);
  };

  const selectedProfile = computed(
    () => profiles.value.find(p => p.id === selectedProfileId.value) || null
  );

  const getSubtipos = () =>
    PROPERTY_TYPES.find(item => item.tipo === state.tipoImovel)?.subtipos || [];

  const setTransaction = value => {
    state.transactionType = value;
  };

  const toggleFeature = key => {
    state.features = { ...state.features, [key]: !state.features[key] };
  };

  const toggleGuarantee = key => {
    state.guarantees = { ...state.guarantees, [key]: !state.guarantees[key] };
  };

  async function loadProfiles() {
    isLoadingProfiles.value = true;
    profilesError.value = '';
    try {
      const { data } = await AgentPublicProfileAPI.postable();
      profiles.value = data?.profiles || [];
      const selectedProfileExists = profiles.value.some(
        profile => profile.id === selectedProfileId.value
      );
      if (profiles.value.length && !selectedProfileExists) {
        // Default: perfil próprio (sector null) quando existir.
        const own = profiles.value.find(p => !p.sector);
        selectedProfileId.value = (own || profiles.value[0]).id;
      }
    } catch {
      profilesError.value =
        'Não foi possível carregar os perfis de publicação.';
    } finally {
      isLoadingProfiles.value = false;
    }
  }

  const isLookingUpCep = ref(false);

  // Lookup de CEP via normalizador de endereço já existente no backend.
  async function lookupCep() {
    const cep = state.cep.replace(/\D/g, '');
    if (cep.length !== 8) {
      errorMessage.value = 'Informe um CEP válido com 8 dígitos.';
      return;
    }
    isLookingUpCep.value = true;
    errorMessage.value = '';
    try {
      const { data } = await ImovelAPI.normalizeAddress({ cep });
      const result = data?.address || data || {};
      updateState({
        estado: result.state || state.estado,
        cidade: result.city || state.cidade,
        bairro: result.neighborhood || state.bairro,
        endereco: state.endereco || result.address || '',
        latitude:
          state.latitude || (result.lat != null ? String(result.lat) : ''),
        longitude:
          state.longitude || (result.lng != null ? String(result.lng) : ''),
      });
    } catch {
      errorMessage.value =
        'Não foi possível consultar o CEP. Preencha o endereço manualmente.';
    } finally {
      isLookingUpCep.value = false;
    }
  }

  async function uploadImages(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    isUploadingImages.value = true;
    errorMessage.value = '';
    try {
      const uploads = await Promise.all(
        files.map(async file => {
          const { fileUrl } = await uploadFile(file, accountId.value);
          return fileUrl;
        })
      );
      state.selectedImages = [
        ...state.selectedImages,
        ...uploads.filter(Boolean),
      ];
    } catch {
      errorMessage.value = 'Falha ao enviar uma ou mais imagens.';
    } finally {
      isUploadingImages.value = false;
    }
  }

  const removeImage = url => {
    state.selectedImages = state.selectedImages.filter(item => item !== url);
  };

  const moveImageToCover = url => {
    state.selectedImages = [
      url,
      ...state.selectedImages.filter(item => item !== url),
    ];
  };

  // Validação por passo (espelha a validação imudei antes de avançar).
  const validateStep = targetStep => {
    if (targetStep === 1) {
      if (!state.selectedImages.length) {
        return 'Adicione pelo menos uma foto do imóvel.';
      }
      return '';
    }
    if (targetStep === 2) {
      if (!state.titulo || state.titulo.trim().length < 10) {
        return 'Título deve ter no mínimo 10 caracteres.';
      }
      if (!state.descricao || state.descricao.trim().length < 50) {
        return 'Descrição deve ter no mínimo 50 caracteres.';
      }
      if (!state.tipoImovel || !state.subTipoImovel) {
        return 'Selecione o tipo e o subtipo do imóvel.';
      }
      if (state.transactionType !== 'Aluguel' && !state.precoVenda) {
        return 'Informe o preço de venda.';
      }
      if (state.transactionType !== 'Venda' && !state.precoAluguel) {
        return 'Informe o preço de aluguel.';
      }
      if (
        state.tipoImovel !== 'Terreno' &&
        !state.areaUtil &&
        !state.areaTotal
      ) {
        return 'Informe a área útil ou a área total.';
      }
      return '';
    }
    return '';
  };

  // Validação final (passo de revisão), incluindo endereço e perfil.
  const validateFinal = () => {
    const stepErrors = validateStep(1) || validateStep(2);
    if (stepErrors) return stepErrors;
    if (!state.cep || state.cep.replace(/\D/g, '').length !== 8) {
      return 'Informe um CEP válido com 8 dígitos.';
    }
    if (!state.cidade || !state.bairro || !state.endereco) {
      return 'Cidade, bairro e logradouro são obrigatórios.';
    }
    if (
      (state.videoUrl && !/^https?:\/\//.test(state.videoUrl)) ||
      (state.tourVirtualUrl && !/^https?:\/\//.test(state.tourVirtualUrl))
    ) {
      return 'Vídeo e tour virtual devem usar URLs válidas (http/https).';
    }
    if (!selectedProfile.value) {
      return 'Selecione o perfil onde o imóvel será publicado.';
    }
    return '';
  };

  const goToStep = target => {
    if (target > step.value) {
      // valida todos os passos até o alvo antes de avançar.
      for (let s = step.value; s < target; s += 1) {
        const err = validateStep(s);
        if (err) {
          errorMessage.value = err;
          return false;
        }
      }
    }
    errorMessage.value = '';
    step.value = target;
    return true;
  };

  const nextStep = () => goToStep(Math.min(step.value + 1, 3));
  const prevStep = () => {
    errorMessage.value = '';
    step.value = Math.max(step.value - 1, 1);
  };

  const toggleOptional = () => {
    showOptional.value = !showOptional.value;
  };

  const selectProfile = id => {
    selectedProfileId.value = id;
  };

  const applyDeepLinkPrefill = query => {
    const { statePatch, profileId } = buildDeepLinkPrefill(query);
    const hasStatePatch = Object.keys(statePatch).length > 0;

    if (!hasStatePatch && profileId === null) return false;

    if (hasStatePatch) updateState(statePatch);
    if (profileId !== null) selectedProfileId.value = profileId;
    deepLinkPrefillApplied.value = true;
    errorMessage.value = '';
    return true;
  };

  // Monta o payload do imóvel com o MAPEAMENTO de colunas reais.
  const buildPayload = () => {
    const caracteristicas = {};
    FEATURES_LIST.forEach(item => {
      if (state.features[item.key]) caracteristicas[item.key] = true;
    });
    Object.entries(state.guarantees).forEach(([key, enabled]) => {
      if (enabled) caracteristicas[key] = true;
    });

    const profile = selectedProfile.value;

    return normalizeImovelPayload(
      {
        titulo: state.titulo.trim(),
        descricao: state.descricao.trim(),
        tipo: state.subTipoImovel || state.tipoImovel,
        finalidade: mapFinalidade(state.transactionType),
        valor_venda: state.precoVenda,
        valor_aluguel: state.precoAluguel,
        cep: state.cep.replace(/\D/g, ''),
        uf: state.estado,
        cidade: state.cidade,
        bairro: state.bairro,
        endereco: state.endereco,
        latitude: state.latitude,
        longitude: state.longitude,
        area_util: state.areaUtil,
        area_total: state.areaTotal,
        quartos: state.qtdDormitorios,
        banheiros: state.qtdBanheiros,
        vagas_garagem: state.qtdVagas,
        valor_iptu: state.iptu,
        valor_condominio: state.valorCondominio,
        additional_attributes: {
          agent_code: profile?.agentCode,
          numero: state.numero || null,
          complemento: state.complemento || null,
          suites: normalizeOptionalInteger(state.qtdSuites),
          ano_construcao: normalizeOptionalInteger(state.anoConstrucao),
          video_url: state.videoUrl || null,
          tour_url: state.tourVirtualUrl || null,
          categoria: state.categoriaImovel,
          zona: state.zona || null,
          registration_flow_source: 'criar_imovel_wizard',
          registration_prefill_source: deepLinkPrefillApplied.value
            ? 'deep_link'
            : null,
          completion_handoff_checklist: COMPLETION_HANDOFF_CHECKLIST.map(
            ({ id, label, owner, routeName, section }) => ({
              id,
              label,
              owner,
              route_name: routeName,
              section,
            })
          ),
          editor_handoff_hint:
            'Após criar, complete estimativa, taxas e acessos/chaves no editor completo.',
        },
        midias: [...state.selectedImages],
        caracteristicas,
      },
      {
        owner: IMOVEL_FORM_OWNER.WIZARD,
        mode: 'create',
      }
    );
  };

  async function publish() {
    if (isPublishing.value) return null;
    const validationError = validateFinal();
    if (validationError) {
      errorMessage.value = validationError;
      return null;
    }

    isPublishing.value = true;
    errorMessage.value = '';
    try {
      const { data } = await ImovelAPI.create(buildPayload());
      const created = data?.imovel || data;
      const imovelId = created?.id;
      // Publica imediatamente quando possível (best-effort).
      if (imovelId) {
        try {
          await ImovelAPI.publish(imovelId);
        } catch {
          // Imóvel já foi criado; publicação pode ser refeita depois.
        }
      }
      localStorage.removeItem(DRAFT_KEY);
      return created || true;
    } catch (error) {
      errorMessage.value =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Não foi possível publicar o imóvel. Tente novamente.';
      return null;
    } finally {
      isPublishing.value = false;
    }
  }

  const resetDraft = () => {
    Object.assign(state, buildBaseState());
    localStorage.removeItem(DRAFT_KEY);
    step.value = 1;
    errorMessage.value = '';
  };

  return {
    state,
    step,
    showOptional,
    isUploadingImages,
    isPublishing,
    errorMessage,
    deepLinkPrefillApplied,
    profiles,
    selectedProfileId,
    selectedProfile,
    isLoadingProfiles,
    profilesError,
    updateState,
    getSubtipos,
    setTransaction,
    toggleFeature,
    toggleGuarantee,
    loadProfiles,
    isLookingUpCep,
    lookupCep,
    uploadImages,
    removeImage,
    moveImageToCover,
    validateStep,
    validateFinal,
    buildPayload,
    goToStep,
    nextStep,
    prevStep,
    toggleOptional,
    selectProfile,
    applyDeepLinkPrefill,
    publish,
    resetDraft,
  };
}
